
import { useMemo } from 'react';
import { 
  useFetchAppItems, 
  useFetchAppFavorites, 
  useToggleAppFavorite 
} from './use-fetch-apps';
export type { AppItem, AppFavorite } from './database-apps';

/**
 * Main hook for app favorites functionality
 * Combines data fetching hooks and provides utility functions
 */
export const useAppFavorites = () => {
  const { data: appItems = [], isLoading: appItemsLoading, error: appItemsError } = useFetchAppItems();
  const { data: userFavorites = [], isLoading: favoritesLoading, error: favoritesError } = useFetchAppFavorites();
  const toggleFavoriteMutation = useToggleAppFavorite();
  
  // Memoize the isAppFavorited function to prevent unnecessary re-renders
  const isAppFavorited = useMemo(() => {
    return (appId: string) => {
      return userFavorites.some(fav => fav.app_id === appId);
    };
  }, [userFavorites]);
  
  // Memoize the getAppFavoriteCount function to prevent unnecessary re-renders
  const getAppFavoriteCount = useMemo(() => {
    return (appId: string) => {
      // Find the app and return its favorite_count
      const app = appItems.find(item => item.id === appId);
      return app?.favorite_count || 0;
    };
  }, [appItems]);
  
  // Memoize the handleToggleFavorite function to prevent unnecessary re-renders
  const handleToggleFavorite = useMemo(() => {
    return async (appId: string) => {
      try {
        const isFavorited = isAppFavorited(appId);
        await toggleFavoriteMutation.mutateAsync({ appId, isFavorited });
      } catch (error) {
        // Error handling is done in the mutation
        console.error('Error toggling app favorite:', error);
      }
    };
  }, [isAppFavorited, toggleFavoriteMutation]);
  
  // Combine all loading states
  const isLoading = appItemsLoading || favoritesLoading;
  const hasError = appItemsError || favoritesError;
  
  // Memoize the enhanced app items with favorited status
  const enhancedAppItems = useMemo(() => {
    return appItems.map(appItem => ({
      ...appItem,
      is_favorited: isAppFavorited(appItem.id),
      onToggleFavorite: () => handleToggleFavorite(appItem.id)
    }));
  }, [appItems, isAppFavorited, handleToggleFavorite]);
  
  return {
    appItems: enhancedAppItems,
    userFavorites,
    isAppFavorited,
    getAppFavoriteCount,
    handleToggleFavorite,
    isLoading,
    isMutating: toggleFavoriteMutation.isPending,
    hasError,
  };
};
